import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Container } from "@/components/shared";
import { ILLUSTRATIONS, getChildName, setChildName } from "@/lib/profiles";
import ChildVoice from "@/pages/homes/ChildVoice";
import KidsBookshelf from "@/components/KidsBookshelf";
import { Mic, HeartCrack, MessageCircle, HelpCircle, Compass, ShieldQuestion, Pencil, Check, MapPin } from "lucide-react";

const CARDS = [
  { id: "hurt", voice: true, icon: HeartCrack, bg: "#FDE1E7", fg: "#B04A63" },
  { id: "said", to: "/reply", icon: MessageCircle, bg: "#DCEBFF", fg: "#3B6FB0" },
  { id: "understand", to: "/analyze", icon: HelpCircle, bg: "#FFF1CC", fg: "#B08A2E" },
  { id: "sorties", to: "/carte-kids", icon: MapPin, bg: "#DCFCE7", fg: "#2E7D5B" },
];

export default function ChildHome() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [name, setName] = useState(getChildName());
  const [editName, setEditName] = useState(false);
  const [draft, setDraft] = useState(name);

  useEffect(() => {
    if (new URLSearchParams(location.search).get("raconte") === "1") setVoiceOpen(true);
  }, [location.search]);

  const closeVoice = () => {
    setVoiceOpen(false);
    if (location.search.includes("raconte")) navigate("/", { replace: true });
  };

  const saveName = () => {
    const v = draft.trim().slice(0, 20);
    setChildName(v); setName(v); setEditName(false);
  };

  const rise = { hidden: { opacity: 0, y: 18 }, show: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }) };

  return (
    <Container className="max-w-3xl">
      <div data-testid="child-home">
        {/* Salutation + illustration + prénom facultatif */}
        <motion.div initial="hidden" animate="show" variants={rise} custom={0}
          className="card-surface p-5 flex items-center gap-4 mb-4" style={{ background: "linear-gradient(135deg,#F3ECFF,#E9F2FF)" }}>
          <img src={ILLUSTRATIONS.childHero} alt="" className="w-28 h-28 object-contain child-bounce shrink-0" data-testid="child-hero-img" />
          <div>
            <h1 className="font-display text-3xl" style={{ color: "var(--accent-strong)" }} data-testid="child-greeting">
              {name ? t("child.greetingNamed", { name }) : t("child.greeting")}
            </h1>
            <p className="text-base mt-1" style={{ color: "var(--text-main)" }}>{t("child.howAreYou")}</p>
          </div>
        </motion.div>

        {/* Prénom (facultatif) */}
        <motion.div initial="hidden" animate="show" variants={rise} custom={1} className="mb-6">
          {editName ? (
            <div className="flex items-center gap-2">
              <input data-testid="child-name-input" value={draft} onChange={(e) => setDraft(e.target.value)}
                placeholder={t("child.namePlaceholder")} maxLength={20}
                onKeyDown={(e) => e.key === "Enter" && saveName()}
                className="flex-1 rounded-2xl px-4 py-2.5 bg-white/80 outline-none text-base"
                style={{ color: "#2C2540", border: "2px solid #CBB8F0" }} autoFocus />
              <button data-testid="child-name-save" onClick={saveName}
                className="w-11 h-11 rounded-2xl flex items-center justify-center text-white shrink-0" style={{ background: "var(--accent-gradient)" }}>
                <Check size={20} />
              </button>
            </div>
          ) : (
            <button data-testid="child-name-edit" onClick={() => { setDraft(name); setEditName(true); }}
              className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 bg-white/70 text-sm" style={{ color: "var(--accent-strong)" }}>
              <Pencil size={15} /> {name ? t("child.changeName") : t("child.addName")}
            </button>
          )}
        </motion.div>

        <motion.h2 initial="hidden" animate="show" variants={rise} custom={2}
          className="font-display text-2xl mb-4" style={{ color: "var(--text-main)" }}>{t("child.whatToDo")}</motion.h2>

        <div className="grid grid-cols-2 gap-4">
          {CARDS.map((c, i) => (
            <motion.button key={c.id} data-testid={`child-card-${c.id}`} onClick={() => (c.voice ? setVoiceOpen(true) : navigate(c.to))}
              initial="hidden" animate="show" variants={rise} custom={i + 3}
              className="rounded-3xl p-5 text-left flex flex-col gap-3 min-h-[132px] transition-transform duration-300 hover:scale-105 active:scale-95 shadow-sm"
              style={{ background: c.bg }}>
              <span className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: "#ffffffaa", color: c.fg }}>
                <c.icon size={26} strokeWidth={2.2} />
              </span>
              <span className="font-display text-lg leading-tight" style={{ color: c.fg }}>{t(`child.cards.${c.id}`)}</span>
            </motion.button>
          ))}
        </div>

        {/* Gros bouton micro — flux vocal direct */}
        <motion.button data-testid="child-mic-cta" onClick={() => setVoiceOpen(true)}
          initial="hidden" animate="show" variants={rise} custom={7}
          className="w-full mt-6 rounded-3xl p-5 flex items-center gap-4 text-left transition-transform duration-300 hover:scale-[1.02] active:scale-95"
          style={{ background: "var(--accent-gradient)" }}>
          <span className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shrink-0 child-bounce">
            <Mic size={30} style={{ color: "var(--accent-strong)" }} />
          </span>
          <span>
            <span className="block font-display text-xl text-white">{t("child.tellMe")}</span>
            <span className="block text-white/90 text-sm">{t("child.tellMeSub")}</span>
          </span>
        </motion.button>

        {/* J'ai besoin d'un adulte */}
        <motion.button data-testid="child-adult-cta" onClick={() => setVoiceOpen(true)}
          initial="hidden" animate="show" variants={rise} custom={8}
          className="w-full mt-4 rounded-3xl p-4 flex items-center gap-3 border-2 transition-colors"
          style={{ borderColor: "#CBB8F0", background: "#F6F1FF", color: "var(--accent-strong)" }}>
          <ShieldQuestion size={22} /> <span className="font-display text-lg">{t("child.needAdult")}</span>
        </motion.button>

        <KidsBookshelf variant="child" />
      </div>

      <ChildVoice open={voiceOpen} onClose={closeVoice} name={name} />
    </Container>
  );
}
